import { SETTINGS_REGISTRY } from '@sentra/contracts';
import { useEffect, useRef } from 'react';

/**
 * AD-54's route-scoped idle lock, on the browser's side of it.
 *
 * **The window belongs to the SCREEN, not to the session.** Five minutes on a gate screen, sixty on
 * an administration one: the gate is a shared terminal with rotating staff, so a long window there
 * attributes a whole shift to whoever signed in first, and every audit row, override reason and
 * acknowledgment then names the wrong human. An administrator reading a report is one person at one
 * desk, and locking them out every five minutes would teach them to keep the screen awake.
 *
 * **The numbers come from the settings registry's shipped defaults, and the SERVER is
 * authoritative.** An operator cannot read `GET /api/v1/settings` — it is an administrator's route
 * — so the browser has no way to learn a value an administrator has changed. What it does instead
 * is lock on the shipped default and honour the server's refusal: `auth/authorize.ts` answers
 * `AUTH_SESSION_IDLE_LOCKED` on the very next request, and `SessionProvider` raises the same
 * overlay from that. So a window an administrator SHORTENED is enforced by the server a moment
 * after the browser would have allowed it, and a window they LENGTHENED locks a little early.
 * Locking early is the safe direction, and it is the only direction this can be wrong in.
 *
 * **A lock is never a logout.** Tripping it changes nothing but the overlay: the route below stays
 * mounted, its form keeps its values, and a PIN puts the operator back exactly where they were.
 */

/** Which window a screen is measured against. `none` is a screen with no session to idle out. */
export type IdleClass = 'gate' | 'admin' | 'none';

/** The shipped windows, read from the one registry that declares them. */
export const IDLE_WINDOW_MINUTES = {
  gate: SETTINGS_REGISTRY['session.idle_minutes_gate'].defaultValue,
  admin: SETTINGS_REGISTRY['session.idle_minutes_admin'].defaultValue,
} as const;

/** How many PIN attempts precede the demand for a full password. */
export const PIN_ATTEMPTS_BEFORE_PASSWORD =
  SETTINGS_REGISTRY['security.pin_attempts_before_password'].defaultValue;

/** How long a screen may sit untouched, or `null` for one that never locks. */
export function idleWindowMs(idle: IdleClass): number | null {
  if (idle === 'none') {
    return null;
  }
  return IDLE_WINDOW_MINUTES[idle] * 60_000;
}

/**
 * What counts as somebody being at the terminal.
 *
 * `keydown` rather than `keyup` so a held key counts immediately, and `pointerdown` rather than
 * `pointermove` so a cat on the desk, a trackpad that drifts, or a page that animates under an
 * unattended cursor does not hold a gate terminal open for a whole shift. `wheel` is here because
 * reading a long list is real activity and involves neither of the other two.
 */
const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'wheel'] as const;

/**
 * Locks the terminal after its screen's window of inactivity.
 *
 * The timer is restarted on activity rather than polled, so an idle terminal costs nothing, and it
 * is torn down whenever the window changes — which is what makes navigating from a gate screen to
 * an administration one re-arm at sixty minutes rather than keep counting at five.
 */
export function useIdleLock(options: {
  readonly idle: IdleClass;
  readonly enabled: boolean;
  readonly onIdle: () => void;
}): void {
  const { idle, enabled, onIdle } = options;
  const callback = useRef(onIdle);
  useEffect(() => {
    callback.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    const declared = idleWindowMs(idle);
    if (!enabled || declared === null) {
      return undefined;
    }
    // Re-bound after the guard rather than used through it: `restart` is a closure, and a narrowing
    // that holds at the guard does not follow a `number | null` into one.
    const windowMs = declared;

    const trip = (): void => {
      callback.current();
    };
    let timer = globalThis.setTimeout(trip, windowMs);
    const restart = (): void => {
      globalThis.clearTimeout(timer);
      timer = globalThis.setTimeout(trip, windowMs);
    };

    for (const name of ACTIVITY_EVENTS) {
      document.addEventListener(name, restart, { passive: true });
    }
    return () => {
      globalThis.clearTimeout(timer);
      for (const name of ACTIVITY_EVENTS) {
        document.removeEventListener(name, restart);
      }
    };
  }, [idle, enabled]);
}
